import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { useAutoAnimate } from '@formkit/auto-animate/react';

import { IProgramEntityAttributes, IUserEntityAttributes, ProgramEntity } from 'Models/Entities';

import SecuredPage from 'Views/Components/Security/SecuredPage';
import Navbar from 'Views/Components/Navbar/Navbar';
import Skeleton, { Colors } from 'Views/Components/Skeleton/Skeleton';
import { Button, Display } from 'Views/Components/Button/Button';
import StudyPlanCollection from 'Views/Components/StudyPlanCollection/StudyPlanCollection';

import { getStudentWithActivePlan } from 'Queries/GraphQlQueries/User/UserEntityQueries';
import { UserStatusFields } from 'Util/UserUtils';
import { userDiscriminatorRoleMap } from 'Models/Enums';
import useMountedQuery from 'Hooks/useMountedQuery';
import { hasAnyRole } from 'Util/SecurityUtils';
import useStore from 'Hooks/useStore';
import ActivateDeactivateButton from 'Views/Pages/Frontend/UserManagement/ActivateDeactivateButton';

export const StudentDetailPage = () => {
	const { userId } = useParams<{ userId: string }>();
	const history = useHistory();
	const store = useStore();

	const { data: userResponse, refresh } = useMountedQuery<Awaited<ReturnType<typeof getStudentWithActivePlan>>>(
		async abortSignal => getStudentWithActivePlan(
			abortSignal,
			[[
				{
					path: 'active',
					comparison: 'equal',
					value: ['true'],
				},
			],
			[
				{
					path: 'studentId',
					comparison: 'equal',
					value: [userId],
				},
			]],
			userId,
		),
		[userId],
	);

	const activeProgram = userResponse?.data?.studyPlanEntity?.programVersion.program;
	const user = userResponse?.data?.userEntity;

	return (
		<SecuredPage
			groups={[
				userDiscriminatorRoleMap.AdminEntity,
				userDiscriminatorRoleMap.InstitutionAdminEntity,
				userDiscriminatorRoleMap.StudentAdvisorEntity,
			]}
		>
			<div className="body-content">
				<Navbar />
				<main className="user-detail-page">
					<div className="action-buttons">
						<Button onClick={() => history.replace('/users?tab=students')}>
							Go back
						</Button>
						<span>
							<Button
								className="edit"
								onClick={() => history.push(`/users/student/edit/${userId}`)}
								display={Display.Outline}
							>
								Edit
							</Button>
							{
								hasAnyRole(store, ['Admin', 'InstitutionAdmin']) && user
									? (
										<ActivateDeactivateButton
											user={user}
											onAfterSave={u => refresh()}
										/>
									)
									: null
							}
						</span>
					</div>
					<StudentDetails
						extendedUser={user}
						activeProgram={activeProgram}
					/>
					<StudyPlanCollection
						userId={userId}
						userDiscriminator="UserEntity"
						studentView={false}
						canDelete={hasAnyRole(store, ['Admin', 'InstitutionAdmin'])}
						showActiveToggle
					/>
				</main>
			</div>
		</SecuredPage>
	);
};

interface IStudentDetailProps {
	extendedUser?: IUserEntityAttributes & UserStatusFields
	activeProgram?: IProgramEntityAttributes | ProgramEntity
}

export const StudentDetails: React.FunctionComponent<IStudentDetailProps> = ({
	extendedUser, activeProgram,
}) => {
	const [animationRef] = useAutoAnimate();

	let content = null;
	if (!extendedUser) { // loading UI
		content = <Skeleton backgroundColor={Colors.WHITE} lines={2} />;
	} else {
		const activeProgramLabel = activeProgram
			? `${activeProgram?.code} - ${activeProgram?.displayName}`
			: 'No active program';

		const lastAccessedLabel = extendedUser.lastAccessed
			? moment(extendedUser.lastAccessed).format('DD/MM/YYYY')
			: 'never';

		content = (
			<>
				<h3>
					{extendedUser.studentId}
					<span className="user-names">
						{extendedUser.firstName || '(No first name)'}{' '}{extendedUser.lastName || '(No last name)'}
					</span>
				</h3>
				<div className="labels">
					<span className="icon icon-left icon-mail">
						{extendedUser.email}
					</span>
					<span className="icon icon-left icon-menu-book">
						{activeProgramLabel}
					</span>
					<span className="last-accessed-date">
						Last accessed:{' '}{lastAccessedLabel}
					</span>
				</div>
			</>
		);
	}

	return (
		<div className="user-details student" data-testid="user-details">
			<div ref={animationRef} key="animation-container">
				{content}
			</div>
		</div>
	);
};
